// Country to Currency mapping (ISO 3166-1 alpha-2 codes)
export const COUNTRY_TO_CURRENCY: Record<string, string> = {
  'IN': 'INR',
  'US': 'USD',
  'GB': 'GBP',
  'AE': 'AED',
  'SG': 'SGD',
  'AU': 'AUD',
  'CA': 'CAD',
  'DE': 'EUR',
  'FR': 'EUR',
  'IT': 'EUR',
  'ES': 'EUR',
  'NL': 'EUR',
  'BE': 'EUR',
  'AT': 'EUR',
  'PT': 'EUR',
  'IE': 'EUR',
  'JP': 'JPY',
  'CN': 'CNY',
  'HK': 'HKD',
  'KR': 'KRW',
  'MY': 'MYR',
  'TH': 'THB',
  'ID': 'IDR',
  'PH': 'PHP',
  'VN': 'VND',
  'NZ': 'NZD',
  'CH': 'CHF',
  'SE': 'SEK',
  'NO': 'NOK',
  'DK': 'DKK',
  'PL': 'PLN',
  'CZ': 'CZK',
  'HU': 'HUF',
  'RO': 'RON',
  'BG': 'BGN',
  'HR': 'EUR',
  'SA': 'SAR',
  'KW': 'KWD',
  'QA': 'QAR',
  'BH': 'BHD',
  'OM': 'OMR',
  'IL': 'ILS',
  'TR': 'TRY',
  'ZA': 'ZAR',
  'EG': 'EGP',
  'NG': 'NGN',
  'KE': 'KES',
  'BR': 'BRL',
  'MX': 'MXN',
  'AR': 'ARS',
  'CL': 'CLP',
  'CO': 'COP',
  'PE': 'PEN',
};

// Currency symbols
export const CURRENCY_SYMBOLS: Record<string, string> = {
  'INR': '₹',
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'AED': 'د.إ',
  'SGD': 'S$',
  'AUD': 'A$',
  'CAD': 'C$',
  'JPY': '¥',
  'CNY': '¥',
  'HKD': 'HK$',
  'KRW': '₩',
  'MYR': 'RM',
  'THB': '฿',
  'IDR': 'Rp',
  'PHP': '₱',
  'VND': '₫',
  'NZD': 'NZ$',
  'CHF': 'CHF',
  'SEK': 'kr',
  'NOK': 'kr',
  'DKK': 'kr',
  'PLN': 'zł',
  'CZK': 'Kč',
  'HUF': 'Ft',
  'RON': 'lei',
  'BGN': 'лв',
  'SAR': 'ر.س',
  'KWD': 'د.ك',
  'QAR': 'ر.ق',
  'BHD': 'د.ب',
  'OMR': 'ر.ع',
  'ILS': '₪',
  'TRY': '₺',
  'ZAR': 'R',
  'EGP': 'E£',
  'NGN': '₦',
  'KES': 'KSh',
  'BRL': 'R$',
  'MXN': 'Mex$',
  'ARS': '$',
  'CLP': '$',
  'COP': '$',
  'PEN': 'S/',
};

// Currency names
export const CURRENCY_NAMES: Record<string, string> = {
  'INR': 'Indian Rupee',
  'USD': 'US Dollar',
  'EUR': 'Euro',
  'GBP': 'British Pound',
  'AED': 'UAE Dirham',
  'SGD': 'Singapore Dollar',
  'AUD': 'Australian Dollar',
  'CAD': 'Canadian Dollar',
  'JPY': 'Japanese Yen',
  'CNY': 'Chinese Yuan',
  'HKD': 'Hong Kong Dollar',
  'KRW': 'South Korean Won',
  'MYR': 'Malaysian Ringgit',
  'THB': 'Thai Baht',
  'IDR': 'Indonesian Rupiah',
  'PHP': 'Philippine Peso',
  'VND': 'Vietnamese Dong',
  'NZD': 'New Zealand Dollar',
  'CHF': 'Swiss Franc',
  'SEK': 'Swedish Krona',
  'NOK': 'Norwegian Krone',
  'DKK': 'Danish Krone',
  'PLN': 'Polish Zloty',
  'CZK': 'Czech Koruna',
  'HUF': 'Hungarian Forint',
  'RON': 'Romanian Leu',
  'BGN': 'Bulgarian Lev',
  'SAR': 'Saudi Riyal',
  'KWD': 'Kuwaiti Dinar',
  'QAR': 'Qatari Riyal',
  'BHD': 'Bahraini Dinar',
  'OMR': 'Omani Rial',
  'ILS': 'Israeli Shekel',
  'TRY': 'Turkish Lira',
  'ZAR': 'South African Rand',
  'EGP': 'Egyptian Pound',
  'NGN': 'Nigerian Naira',
  'KES': 'Kenyan Shilling',
  'BRL': 'Brazilian Real',
  'MXN': 'Mexican Peso',
  'ARS': 'Argentine Peso',
  'CLP': 'Chilean Peso',
  'COP': 'Colombian Peso',
  'PEN': 'Peruvian Sol',
};

// Detect country from IP using ipapi.co (free, no API key needed)
// IP Geolocation services with fallback
const IP_SERVICES = [
  {
    name: 'ipapi.co',
    url: 'https://ipapi.co/json/',
    extract: (data: any) => data.country,
    timeout: 3000
  },
  {
    name: 'country.is',
    url: 'https://api.country.is/',
    extract: (data: any) => data.country,
    timeout: 3000
  },
  {
    name: 'ipwhois.app',
    url: 'https://ipwhois.app/json/',
    extract: (data: any) => data.country_code,
    timeout: 3000
  }
];

export const detectCountryFromIP = async (): Promise<string> => {
  // Try each service in order until one succeeds
  for (const service of IP_SERVICES) {
    try {
      console.log(`Trying ${service.name} for country detection...`);
      
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), service.timeout);
      
      const response = await fetch(service.url, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        console.warn(`${service.name} returned status ${response.status}`);
        continue;
      }
      
      const data = await response.json();
      
      // Check for error responses (some APIs return 200 with error object)
      if (data.error || data.message?.includes('rate limit')) {
        console.warn(`${service.name} returned error:`, data.message || data.reason);
        continue;
      }
      
      const country = service.extract(data);
      
      if (country && typeof country === 'string' && country.length === 2) {
        console.log(`✅ Country detected: ${country} (via ${service.name})`);
        return country.toUpperCase();
      }
      
      console.warn(`${service.name} returned invalid country code:`, country);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.warn(`${service.name} timed out after ${service.timeout}ms`);
      } else {
        console.warn(`${service.name} failed:`, error.message);
      }
      // Continue to next service
    }
  }
  
  // All services failed - return empty string (don't default to IN)
  console.error('All IP detection services failed, returning empty');
  return '';
};

// Get currency for a country
export const getCurrencyForCountry = (country: string): string => {
  return COUNTRY_TO_CURRENCY[country] || 'INR';
};

// Get currency symbol
export const getCurrencySymbol = (currency: string): string => {
  return CURRENCY_SYMBOLS[currency] || currency;
};

// Get currency name
export const getCurrencyName = (currency: string): string => {
  return CURRENCY_NAMES[currency] || currency;
};

// Format amount with currency
export const formatCurrency = (amount: number, currency: string): string => {
  const symbol = getCurrencySymbol(currency);
  // Remove decimals for whole numbers
  return `${symbol}${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
};

// Convert currency using live exchange rates
let exchangeRatesCache: { rates: Record<string, number>; timestamp: number } | null = null;
const CACHE_DURATION = 3600000; // 1 hour in milliseconds

export const convertCurrency = async (
  amount: number,
  fromCurrency: string,
  toCurrency: string
): Promise<number> => {
  if (fromCurrency === toCurrency) return amount;

  try {
    // Check cache
    const now = Date.now();
    if (exchangeRatesCache && (now - exchangeRatesCache.timestamp) < CACHE_DURATION) {
      const rate = exchangeRatesCache.rates[toCurrency];
      if (rate) return amount * rate;
    }

    // Fetch fresh rates from INR base
    const response = await fetch(`https://api.exchangerate-api.com/v4/latest/${fromCurrency}`);
    if (!response.ok) throw new Error('Exchange rate API failed');
    
    const data = await response.json();
    
    // Cache the rates
    exchangeRatesCache = {
      rates: data.rates,
      timestamp: now
    };

    const rate = data.rates[toCurrency];
    if (!rate) throw new Error(`Exchange rate not found for ${toCurrency}`);
    
    return amount * rate;
  } catch (error) {
    console.error('Currency conversion failed:', error);
    // Fallback to approximate rates if API fails
    const fallbackRates: Record<string, number> = {
      'INR': 1,
      'USD': 0.012,
      'EUR': 0.011,
      'GBP': 0.0095,
      'AED': 0.044,
      'SGD': 0.016,
      'AUD': 0.018,
      'CAD': 0.016,
    };
    
    if (fromCurrency === 'INR' && fallbackRates[toCurrency]) {
      return amount * fallbackRates[toCurrency];
    }
    
    // If all else fails, return original amount
    return amount;
  }
};

// Country code to name mapping
export const COUNTRY_CODE_TO_NAME: Record<string, string> = {
  'IN': 'India',
  'US': 'United States',
  'GB': 'United Kingdom',
  'AE': 'United Arab Emirates',
  'SG': 'Singapore',
  'AU': 'Australia',
  'CA': 'Canada',
  'DE': 'Germany',
  'FR': 'France',
  'IT': 'Italy',
  'ES': 'Spain',
  'NL': 'Netherlands',
  'BE': 'Belgium',
  'AT': 'Austria',
  'PT': 'Portugal',
  'IE': 'Ireland',
  'JP': 'Japan',
  'CN': 'China',
  'HK': 'Hong Kong',
  'KR': 'South Korea',
  'MY': 'Malaysia',
  'TH': 'Thailand',
  'ID': 'Indonesia',
  'PH': 'Philippines',
  'VN': 'Vietnam',
  'NZ': 'New Zealand',
  'CH': 'Switzerland',
  'SE': 'Sweden',
  'NO': 'Norway',
  'DK': 'Denmark',
  'PL': 'Poland',
  'CZ': 'Czech Republic',
  'HU': 'Hungary',
  'RO': 'Romania',
  'BG': 'Bulgaria',
  'HR': 'Croatia',
  'SA': 'Saudi Arabia',
  'KW': 'Kuwait',
  'QA': 'Qatar',
  'BH': 'Bahrain',
  'OM': 'Oman',
  'IL': 'Israel',
  'TR': 'Turkey',
  'ZA': 'South Africa',
  'EG': 'Egypt',
  'NG': 'Nigeria',
  'KE': 'Kenya',
  'BR': 'Brazil',
  'MX': 'Mexico',
  'AR': 'Argentina',
  'CL': 'Chile',
  'CO': 'Colombia',
  'PE': 'Peru',
};

export const getCountryName = (countryCode: string): string => {
  return COUNTRY_CODE_TO_NAME[countryCode] || countryCode;
};
